import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { FormDialog } from '@/components/composed/FormDialog';
import { FormField } from '@/components/composed/FormField';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ApiError } from '@/lib/api/errors';
import { registeredFeaturesQuery } from '../api/permission-queries';
import { useDefinePermission } from '../api/permission-mutations';

/*
 * The define-permission surface (F03 R41). Built for task 2.2 against the
 * task-2.1 tests in DefinePermissionDialog.test.tsx:
 *
 *   - AC-d8754ca3 (tpa half) — WHEN an administrator defines a permission, the
 *     kind of record is CHOSEN FROM the features already registered with
 *     Identity (not free text), and the action is accepted as the WORDS they
 *     type (a free-text field). The record-kind is a native <select> whose
 *     options are exactly the registered features (so the chooser offers only
 *     what Identity would accept); the action is a free-text input, so an action
 *     on no list can still be entered. The identity half — Identity's
 *     unknown-resource FK rejection and the duplicate refusal — is the server's,
 *     relayed by the domain service; this surface is the courtesy half and
 *     surfaces the server's refusal where the operator works.
 *   - AC-9d86cbd6 (consumed) — the description is OPTIONAL; a permission saved
 *     with none is accepted by Identity, so the field may be left empty.
 *
 * The record-kind + free-text-action shape is resolution #2 (Rich Theil,
 * 2026-09-28): the governed record-kind, the free-text action; the export's
 * earlier "do not add a dropdown" warning is superseded. A native <select> (as
 * the account-population org chooser does) is used deliberately — the record-
 * kind is a one-of-a-known-set choice bound to Identity's registered features,
 * and a native control keeps the "choose, don't type" contract plain.
 *
 * Mutate → invalidate: `useDefinePermission` posts the definition and, on
 * success, invalidates the catalog so the new permission appears. No optimistic
 * update. A duplicate `resource.action` is a 409 Identity returns with the
 * catalog unchanged; the mutation does not swallow it — the refusal is surfaced
 * in an in-dialog alert and the dialog stays open (the save is a failure, never
 * shown as success). The refusal text is the server's, rendered verbatim; this
 * surface composes no cause-specific wording of its own.
 *
 * F03 records; F03b refuses — nothing here is an authorization decision.
 */
const NO_RECORD_KIND = '';

export function DefinePermissionDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement | null {
  // The features already registered with Identity — the only valid record-kinds
  // (AC-d8754ca3). The chooser offers exactly these.
  const { data } = useQuery(registeredFeaturesQuery());
  const registeredFeatures = data ?? [];

  const definePermission = useDefinePermission();

  // The chosen record-kind (a registered feature's key) and the free-text
  // action and optional description. Local UI state: the record-kind is chosen
  // from the offered features, the action typed freely.
  const [resource, setResource] = useState<string>(NO_RECORD_KIND);
  const [action, setAction] = useState<string>('');
  const [description, setDescription] = useState<string>('');

  // The server's refusal, surfaced where the operator works (e.g. the 409
  // duplicate Identity returns with the catalog unchanged). Held as the server's
  // own text; cleared on a fresh submit.
  const [refusal, setRefusal] = useState<string | null>(null);

  if (!open) return null;

  // The courtesy half: a record-kind must be chosen and an action typed before
  // the save is offered. The server (Identity, relayed by the domain service)
  // is the authority — the FK check on the record-kind and the duplicate
  // refusal are its call; this only withholds the submit until the two parts
  // are present.
  const canSubmit = resource !== NO_RECORD_KIND && action.trim().length > 0;

  function handleSubmit(): void {
    setRefusal(null);
    definePermission.mutate(
      {
        resource,
        action: action.trim(),
        // Optional: omit when empty so Identity accepts the no-description save
        // (AC-9d86cbd6) and the catalog shows a description only where present
        // (AC-3ce30755).
        ...(description.trim() ? { description: description.trim() } : {}),
      },
      {
        onSuccess: () => {
          setResource(NO_RECORD_KIND);
          setAction('');
          setDescription('');
          onOpenChange(false);
        },
        onError: (error) => {
          // Surface the server's refusal verbatim where the operator works and
          // keep the dialog open — the save failed, so nothing is shown as
          // created. One generic alert regardless of cause; the text is the
          // server's.
          setRefusal(
            error instanceof ApiError
              ? error.message
              : 'That permission could not be defined.',
          );
        },
      },
    );
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Define permission"
      onSubmit={handleSubmit}
      submitLabel="Define"
      pending={definePermission.isPending}
      submitDisabled={!canSubmit}
    >
      {/* The server's refusal, where the operator works (AC-de3346c0 relayed):
          a duplicate `resource.action` is a 409 with the catalog unchanged. The
          text is the server's; the dialog stays open so the save is never shown
          as success. */}
      {refusal ? (
        <Alert variant="destructive" role="alert">
          <AlertDescription>{refusal}</AlertDescription>
        </Alert>
      ) : null}

      {/* The record-kind — CHOSEN from the features registered with Identity,
          not typed (AC-d8754ca3). A native <select> so the choice is bound to a
          known set; its options are exactly the registered features, so the
          chooser offers only what Identity would accept. */}
      <FormField label="Kind of record">
        {({ id }) => (
          <select
            id={id}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={resource}
            onChange={(e) => setResource(e.target.value)}
          >
            <option value={NO_RECORD_KIND}>Choose a kind of record…</option>
            {registeredFeatures.map((feature) => (
              <option key={feature.key} value={feature.key}>
                {feature.label}
              </option>
            ))}
          </select>
        )}
      </FormField>

      {/* The action — accepted as the WORDS the administrator types
          (AC-d8754ca3): a free-text field, so an action on no list can still be
          entered. */}
      <FormField label="Action">
        {({ id }) => (
          <Input
            id={id}
            value={action}
            onChange={(e) => setAction(e.target.value)}
          />
        )}
      </FormField>

      {/* The description — OPTIONAL (AC-9d86cbd6): a permission saved with none
          is accepted. Shown against the permission in the catalog only where
          present (AC-3ce30755). */}
      <FormField label="Description">
        {({ id }) => (
          <Textarea
            id={id}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        )}
      </FormField>
    </FormDialog>
  );
}
